// Apple In-App Purchases (StoreKit 2) — JS side of the native AppleIAP plugin.
// Only functional inside the native iOS app; all purchase verification
// happens server-side (/api/billing/apple/*).
import { registerPlugin } from "@capacitor/core";
import { api } from "@/lib/http";
import { isIOSNative } from "@/lib/platform";

const AppleIAP = registerPlugin("AppleIAP");

export const PRODUCTS = {
  monthly: "com.solano.essence.pro.monthly",
  yearly: "com.solano.essence.pro.yearly",
};

export async function loadAppleProducts() {
  // Sandbox/StoreKit is flaky right after install: retry (also on EMPTY
  // results) with a short backoff before giving up — Apple review loads
  // the paywall seconds after first launch.
  let lastErr = null;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const res = await AppleIAP.products();
      console.log("[StoreKit] products result:", JSON.stringify(res?.debug || {}), "count:", res?.products?.length ?? 0);
      if (res?.products?.length) return res.products;
      lastErr = new Error("EMPTY_PRODUCTS");
    } catch (e) {
      console.error(`[StoreKit] products() attempt ${attempt} failed:`, e?.message || e);
      lastErr = e;
    }
    if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 1500));
  }
  throw lastErr || new Error("EMPTY_PRODUCTS");
}

/** Hidden diagnostics (5 taps on the paywall badge): why did StoreKit return nothing? */
export async function storeKitDiagnostics() {
  try {
    const res = await AppleIAP.products();
    return { ok: true, ...(res?.debug || {}), loaded: (res?.products || []).map((p) => `${p.id} ${p.displayPrice}`) };
  } catch (e) {
    return { ok: false, error: String(e?.message || e), code: e?.code || null };
  }
}

async function submitSignedTransaction(signedTransaction) {
  const { data } = await api.post("/billing/apple/transactions", { signedTransaction });
  return data;
}

/** Full purchase flow: account token → StoreKit sheet → server verification.
 *  Returns {status: 'success'|'cancelled'|'pending', active?} */
export async function purchaseAppleSubscription(productId) {
  const { data: tok } = await api.post("/billing/apple/account-token");
  const res = await AppleIAP.purchase({ productId, appAccountToken: tok.appAccountToken });
  if (res?.status === "success" && res.signedTransaction) {
    const verified = await submitSignedTransaction(res.signedTransaction);
    return { status: "success", active: verified?.active };
  }
  return { status: res?.status || "cancelled" };
}

/** "Käufe wiederherstellen": AppStore.sync + re-submit current entitlements. */
export async function restoreApplePurchases() {
  await AppleIAP.restore();
  const { transactions } = await AppleIAP.entitlements();
  let active = 0;
  for (const tx of transactions || []) {
    if (!tx.signedTransaction) continue;
    try {
      const r = await submitSignedTransaction(tx.signedTransaction);
      if (r?.active) active += 1;
    } catch { /* one bad tx must not block the rest */ }
  }
  return { restored: (transactions || []).length, active };
}

export async function manageAppleSubscription() {
  await AppleIAP.manage();
}

let listenerStarted = false;
/** Forwards StoreKit Transaction.updates (renewals, other devices, unfinished
 *  purchases) to the backend. Call once at app start; no-op on web. */
export function initAppleIAPListener() {
  if (!isIOSNative() || listenerStarted) return;
  listenerStarted = true;
  try {
    const sub = AppleIAP.addListener("transaction", (data) => {
      if (data?.signedTransaction) {
        submitSignedTransaction(data.signedTransaction).catch(() => {});
      }
    });
    if (sub && typeof sub.catch === "function") sub.catch(() => {});
  } catch { /* plugin missing (old build) */ }
}
